import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import {callAlertWeb, callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb} from 'common/appInterface';
import {CardProductTabName} from "../../constants/Card";
import { WEBVIEW_RESOURCE_VERSION } from 'common/cacheOverwrite';
import CardApplyBlockModal from 'components/CardApplyBlockModal';
import { fetchRestrictedCountryCodes, isRestrictedCountry } from 'common/cardApplyRestriction';

const ProductTabPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL02";
  const screenName = t("APPL02.title");

  const [productList, setProdcutList] = useState<Map<string, Product>>();
  const [debitState, setDebitState] = useState<CardDebitState | null>(null);

  /** 카드상품 조회 */
  const callProductList = useCallback(async () => {
    showProgressWeb();
    await client.get("/api/app/product", { params: location.state.query }).then(response => {
      let productMap = new Map<string, Product>();
      response.data.response.filter((value: Product) => value.useYn === "Y" || value.hasYn === "Y" || value.receiveYn === "Y").forEach((value: Product) => {
        productMap.set(value.cardProductCode, value);
      });
      setProdcutList(productMap);
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location]);

  /** 출금동의여부조회 */
  const callDebitState = useCallback(async () => {
    await client.get('/api/app/manage/debit/state', {
      params: {
        ...location.state.query
      }
    }).then(response => {
      setDebitState(response.data.response);
    }).catch((e: AxiosError) => {
      callAlertWeb('ERROR', '99', e.message);
    });
  }, [location.state.query]);


  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    callProductList();
    callDebitState();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {productList && <ProductTabChild productList={productList} debitState={debitState} />}
    </>
  );
};

const ProductTabChild: React.FC<{ productList: Map<string, Product>, debitState: CardDebitState | null }> = ({ productList, debitState }) => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t } = useTranslation();

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep2>>({ ...location.state.apply });
  const [cardProductCode, setCardProductCode] = useState(Array.from(productList.values())[0].cardProductCode);
  /** 발급 제한 국가코드 목록 */
  const [restrictedCountryCodes, setRestrictedCountryCodes] = useState<string[]>([]);
  /** 발급 제한 국가 안내 팝업 */
  const [isApplyBlockModal, setIsApplyBlockModal] = useState<boolean>(false);

  /** backKeyEvent_App 처리 (APP > WEB) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    setCardApply((value) => ({ ...value, cardProductCode: cardProductCode }));
  }, [cardProductCode]);

  // 발급 제한 국가 목록 조회
  useEffect(() => {
    fetchRestrictedCountryCodes(location.state.query.languageCode).then(setRestrictedCountryCodes);

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 카드신청 클릭 */
  const clickApply = useCallback(() => {
    showProgressWeb();

    /** 고객정보 조회 */
    client.get("/api/app/apply/info", { params: {...location.state.query, cardProductCode: cardProductCode}}).then((response) => {
      let result = response.data.response;

      if (response.data.code === "00") {
        // 발급 제한 국가
        if (isRestrictedCountry(result.countryCode, restrictedCountryCodes)) {
          setIsApplyBlockModal(true);
          return;
        }
        // KYC 미인증
        if (result.kycApproved === "N") {
          callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
        }
        else {
          navigate("/apply/step0", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardProductCode}, apply: { ...cardApply, userEngName: result.userEngName, phoneNo: result.phoneNo, sexDivisionCode: result.sexDivisionCode, birthDate: result.birthDate, foreignerDivisionCode: result.foreignerDivisionCode }, userInfo: debitState}});
        }
      }
      else {
        callAlertWeb("ERROR", response.data.code, response.data.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location, navigate, cardApply, t, debitState, cardProductCode, restrictedCountryCodes]);

  // APP이벤트 공통 수신처리 (APP > WEB)
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents">
          <div className="tabWrap">
            <div className="btnTabWrap">
              {Array.from(productList.values()).map((value) => (
                <div
                  key={value.cardProductCode}
                  className={
                    cardProductCode === value.cardProductCode
                      ? 'btnTab on'
                      : 'btnTab'
                  }
                  onClick={() => setCardProductCode(value.cardProductCode)}>
                  <button type="button">
                    <span>
                      {CardProductTabName[Number(value.cardProductCode)]}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="inner">
            <div className="section serverWrap">
              <iframe
                className="productTabDetail"
                key={cardProductCode}
                src={`${process.env.REACT_APP_OUTER_HTML_PATH}/card/cardhtml/html/${location.state.query.languageCode}/${cardProductCode}_benefit.html?v=${WEBVIEW_RESOURCE_VERSION}`}
                allowFullScreen={true}
                title={productList?.get(cardProductCode)?.cardProductCodeName}
              />
            </div>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button
              type="button"
              className="btnPrimaryTy01"
              onClick={clickApply}
              disabled={
                !productList || productList?.get(cardProductCode)?.hasYn === 'Y'
                  ? true
                  : productList?.get(cardProductCode)?.receiveYn === 'Y'
              }>
              <span>
                {productList?.get(cardProductCode)?.hasYn === 'Y'
                  ? t('APPL02.exist')
                  : productList?.get(cardProductCode)?.receiveYn === 'Y'
                  ? t('APPL02.receive')
                  : t('APPL02.apply')}
              </span>
            </button>
          </div>
        </div>
      </div>
      <CardApplyBlockModal isOpen={isApplyBlockModal} onClose={() => setIsApplyBlockModal(false)} />
    </div>
  );
};

export default ProductTabPage;